import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ui.module.css';
import { Icon } from './Icon';

const MAX = 9999;

/** Decimal-safe quantity as a string (API contract: "0.5", "2"). Step buttons move by 1. */
export function QuantityStepper({
  value,
  onChange,
  label,
  error,
}: {
  value: string;
  onChange(value: string): void;
  label: string;
  error?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const n = Number(value);
  const step = (delta: number) => {
    const base = Number.isFinite(n) ? n : 0;
    const next = Math.min(MAX, Math.max(1, Math.round((base + delta) * 1000) / 1000));
    onChange(String(next));
  };
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div className={styles.stepper}>
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={!(n > 1)}
          aria-label={t('items.decrease')}
        >
          <Icon name="minus" />
        </button>
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))}
        />
        <button
          type="button"
          onClick={() => step(1)}
          disabled={n >= MAX}
          aria-label={t('items.increase')}
        >
          <Icon name="plus" />
        </button>
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Mirrors QuantitySchema: > 0, ≤ 9999, ≤ 3 decimals. */
export function isValidQuantity(value: string): boolean {
  if (!/^\d{1,4}(\.\d{1,3})?$/.test(value)) return false;
  const n = Number(value);
  return n > 0 && n <= MAX;
}
